/**
 * Educational (academic) year helpers.
 *
 * An educational year runs 01-Sep of year Y through 31-Aug of year Y+1 and is labelled "Y/Y+1".
 * The options are generated from the calendar rather than read from the `school_periods` table:
 * real periods differ per school, overlap, and their names are free text, so they cannot drive a
 * consistent year filter.
 */

const SEPTEMBER = 8 // zero-based month index
const LAST_DAY_OF_AUGUST = 31

// Oldest school period on record starts in 2012, so nothing earlier can have groups.
const FIRST_EDUCATIONAL_YEAR = 2012
const YEARS_FORWARD = 1 // next year, so groups created for the upcoming year stay reachable

const pad = value => String(value).padStart(2, '0')

const toIsoDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/**
 * Start year of the educational year the given date belongs to.
 * September and later belong to the year that just started, January-August to the previous one.
 */
export const getEducationalYearStart = (date = new Date()) => (
  date.getMonth() >= SEPTEMBER ? date.getFullYear() : date.getFullYear() - 1
)

/** Inclusive 01-Sep..31-Aug bounds of the educational year starting in `startYear`, as ISO dates. */
export const getEducationalYearBounds = startYear => ({
  startDate: toIsoDate(new Date(startYear, SEPTEMBER, 1)),
  endDate: toIsoDate(new Date(startYear + 1, SEPTEMBER - 1, LAST_DAY_OF_AUGUST))
})

/** Display label of an educational year, e.g. 2025 -> "2025/2026". */
export const getEducationalYearName = startYear => `${startYear}/${startYear + 1}`

/**
 * Selector options, newest first, from next year back to the first year the platform holds data for.
 * Shaped as `value`/`name` for the shared SelectField `optionsWithValues` prop.
 */
export const buildEducationalYearOptions = (date = new Date()) => {
  const latest = getEducationalYearStart(date) + YEARS_FORWARD
  const length = Math.max(latest - FIRST_EDUCATIONAL_YEAR + 1, 1)

  return Array.from({ length }, (_, index) => {
    const startYear = latest - index
    return {
      value: startYear,
      name: getEducationalYearName(startYear)
    }
  })
}
